import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {getLanguageLabel} from 'src/components/settings/language-support';
import {languageMenuOptions} from 'src/constants/language-support';
import {useDispatch, useSelector} from 'react-redux';
import {sendMessageToExtension} from 'src/components/micro-apps-extension/utils';
import {UPDATE_I18N_SELECTED_LANGUAGE} from 'src/chat-extension/messages';
import * as reduxApi from 'src/utils/redux-api-v2';
import {selectAgencyConfigFlag} from 'src/selectors/agency';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {ButtonTextSmall} from '@spaced-out/ui-design-system/lib/components/Text';
import {CustomDropdown} from 'src/components/lib/custom-dropdown/custom-dropdown';
import {SideMenuLink} from '@spaced-out/ui-design-system/lib/components/SideMenuLink';

import css from './support-links.css';
import {MenuOption} from '@spaced-out/ui-design-system';


const optionMappings = {
  featureRequest:
    'https://form.asana.com/?k=HHJwBxMRKYi4zwNiU_qYVg&d=713022220213973',
  fileAnIssue: 'https://support.sensehq.co/hc/en-us/requests/new',
} as const;

const SupportLinks = () => {
  const dispatch = useDispatch();
  const {updateLanguage, t, getCurrentLanguage} = useLabelTranslation();

  const [activeMenu, setActiveMenu] = React.useState('custom');

  const finalResolvedLanguage = getCurrentLanguage({
    supportLocalStorageLanguage: false,
  });

  const [selectedLanguage, setSelectedLanguage] = React.useState({
    key: finalResolvedLanguage,
    label: getLanguageLabel(finalResolvedLanguage),
  });

  React.useEffect(() => {
    setLanguage(finalResolvedLanguage, false, true);
  }, [finalResolvedLanguage]);

  const handleSenseUniversityOnClick = async () => {
    // @ts-expect-error - TS2339 - Property 'then' does not exist on type 'ThunkAction<any>'. | TS7006 - Parameter 'url' implicitly has an 'any' type.
    await dispatch(reduxApi.get('sense-university/login')).then((url) => {
      window.open(url, '_blank');
    });
  };

  const languageSupportEnabled = useSelector((state) =>
    selectAgencyConfigFlag(state, 'i18NEnableLanguageSupport'),
  );

  const helpDropdownOptions = [
    {
      key: 'senseLearningCenter',
      label: t('Sense_Learning_Center', 'Sense Learning Center'),
    },
    {
      key: 'featureRequest',
      label: t('Feature_Request', 'Feature Request'),
    },
    ...(languageSupportEnabled
      ? [
          {
            key: 'language_support',
            label: `${t('Language', 'Language')} : ${selectedLanguage.label}`,
            iconLeft: 'language',
            iconRight: 'chevron-right',
          },
        ]
      : []),
    {
      key: 'fileAnIssue',
      label: t('File_an_Issue', 'File an Issue'),
    },
  ];

  const updatedLanguageOptions = languageMenuOptions.map((option) => ({
    ...option,
    iconRight: option.key === selectedLanguage.key ? 'check' : '',
  }));

  const setLanguage = async (
    language: string,
    persistLanguage: boolean,
    sendToExtension: boolean,
  ) => {
    await updateLanguage({language, persistLanguage});
    setSelectedLanguage({
      key: language,
      label: getLanguageLabel(language),
    });

    if (sendToExtension) {
      sendMessageToExtension(UPDATE_I18N_SELECTED_LANGUAGE, language);
    }
  };

  const onLanguageSelect = (option: MenuOption) => {
    setLanguage(option.key, true, true);
    setActiveMenu('custom');
  };

  const onOptionSelect = async (option: MenuOption) => {
    if (option.key === 'senseLearningCenter') {
      await handleSenseUniversityOnClick();
    } else if (option.key === 'language_support') {
      setActiveMenu('language');
    } else {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly featureRequest: "https://form.asana.com/?k=HHJwBxMRKYi4zwNiU_qYVg&d=713022220213973"; readonly fileAnIssue: "https://support.sensehq.co/hc/en-us/requests/new"; }'.
      const url = optionMappings[option.key];
      url && window.open(url, '_blank');
    }
  };

  return (
    <div className={css.sideNavHelp}>
      <CustomDropdown
        anchorPosition="right-end"
        menu={{
          isFluid: false,
          menuDisabled: false,
          options:
            activeMenu === 'custom'
              ? helpDropdownOptions
              : updatedLanguageOptions,
          selectedKeys: [selectedLanguage.key],
          header: activeMenu === 'language' && (
            <div className={css.menuHeader}>
              <Button
                type="ghost"
                iconLeftName="chevron-left"
                size="small"
                onClick={() => setActiveMenu('custom')}
              />
              <ButtonTextSmall>
                {t('Choose_Language', 'Choose Language')}
              </ButtonTextSmall>
            </div>
          ),
        }}
        onOptionSelect={
          activeMenu === 'language' ? onLanguageSelect : onOptionSelect
        }
        tooltip={{
          body: t('Support_Links', 'Support Links'),
          classNames: {tooltip: css.toolTip},
        }}
      >
        <SideMenuLink
          classNames={{wrapper: css.sideMenuLinkWrapper}}
          pageNameKey="supportLinks"
        />
      </CustomDropdown>
    </div>
  );
};

export default SupportLinks;
